import { TimeUnit } from "@amcharts/amcharts5/.internal/core/util/Time";

import { metricsDictionary } from "@/settings/metricsDictionary";
import { ChartDataRecord } from "@/types/charts";

export const EMPIRIC_CRITICAL_DATA_RANGE = 9;

export const TIME_UNIT: Record<"YEAR" | "MONTH" | "WEEK" | "DAY", TimeUnit> = {
  YEAR: "year",
  MONTH: "month",
  WEEK: "week",
  DAY: "day",
};

export const STATS_METRICS = ["max", "75th", "median", "25th", "min"];

export const mapToRange = (value: number, inMin: number, inMax: number, outMin: number, outMax: number): number => {
  const range = inMax - inMin;
  if (range <= 0) return outMin;
  const t = Math.max(0, Math.min(1, (value - inMin) / range));
  return outMin + t * (outMax - outMin);
};

export const getChartInterval = (axis: string): { timeUnit: string; count: number } => {
  const defaultInterval = { timeUnit: TIME_UNIT.MONTH, count: 1 };
  let interval;

  switch (axis) {
    case "year":
      interval = { timeUnit: TIME_UNIT.YEAR, count: 1 };
      break;
    case "quarter":
      interval = { timeUnit: TIME_UNIT.MONTH, count: 3 };
      break;
    case "month":
      interval = defaultInterval;
      break;
    case "week":
      interval = { timeUnit: TIME_UNIT.WEEK, count: 1 };
      break;
    case "day":
      interval = { timeUnit: TIME_UNIT.DAY, count: 1 };
      break;
    default:
      interval = defaultInterval;
  }

  return interval;
};

export const transformMetricName = (string: string): string => {
  const metricDictionary = metricsDictionary.find((metric) => {
    return metric.value === string;
  });

  return metricDictionary?.title as string;
};

export const getFormatter = (
  settings: Record<string, unknown>
): ((val: string | number | null) => string | number) | undefined => {
  const metricDictionary = metricsDictionary.find((metric) => {
    return metric.value === settings.displayMetric;
  });

  if (metricDictionary && metricDictionary.formatter) {
    const { formatter } = metricDictionary;

    return (val: string | number | null) => {
      return settings.compactFormat ? formatter(val, { notation: "compact" }) : formatter(val);
    };
  }
};

export const countZeroes = (value: number) =>
  (value || 0)
    .toString()
    .replace(".", "")
    .replace(/[^0].*/g, "").length;

// This function can be useful in the future.
export const calcDataRange = (data: ChartDataRecord[], key: string): number => {
  const values = data.map((item) => Number(item?.[key] ?? 0));

  return Math.max(...values) - Math.min(...values);
};

export const calcDataRangeMaxMin = (values: Record<string, unknown>): number => {
  const valuesArray: number[] = [];

  for (const key in values) {
    const value = values[key];

    if (typeof value === "number") {
      valuesArray.push(value);
    }
  }

  return Math.max(...valuesArray) - Math.min(...valuesArray);
};

export const calculateTooltipValuesAccuracy = (tooltipValues: Record<string, unknown>): number => {
  const zeros = countZeroes(calcDataRangeMaxMin(tooltipValues));
  return zeros > 1 ? zeros + 1 : 0;
};
